import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";
import JobModeration from "./components/JobModeration";
import BusinessManagement from "./components/BusinessManagement";
import UserManagement from "./components/UserManagement";
import AdminRoleManagement from "./components/AdminRoleManagement";
import PostJobForm from "./components/PostJobForm";

type AdminTab = "overview" | "jobs" | "post" | "businesses" | "users" | "adminRoles";

interface OverviewStats {
  pendingJobs: number;
  approvedJobs: number;
  totalJobs: number;
  totalBusinesses: number;
  totalUsers: number;
  totalAdmins: number;
  totalProfiles: number;
}

export default function AdminDashboard() {
  const { user, loading, isAuthenticated, refreshProfile } = useAuth();
  const [activeTab, setActiveTab] = useState<AdminTab>("overview");
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [mobileSidebarClosing, setMobileSidebarClosing] = useState(false);
  const [overviewStats, setOverviewStats] = useState<OverviewStats>({
    pendingJobs: 0, approvedJobs: 0, totalJobs: 0,
    totalBusinesses: 0, totalUsers: 0, totalAdmins: 0, totalProfiles: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [profileVerified, setProfileVerified] = useState(false);

  const fetchOverviewStats = useCallback(async () => {
    try {
      const [jobsRes, pendingRes, approvedRes, bizRes, userRes, adminRes, totalRes] = await Promise.all([
        supabase.from("vietnamcv_jobs").select("id", { count: "exact", head: true }),
        supabase.from("vietnamcv_jobs").select("id", { count: "exact", head: true }).eq("status", "pending"),
        supabase.from("vietnamcv_jobs").select("id", { count: "exact", head: true }).eq("status", "approved"),
        supabase.from("vietnamcv_profiles").select("id", { count: "exact", head: true }).contains("roles", ["business"]),
        supabase.from("vietnamcv_profiles").select("id", { count: "exact", head: true }).contains("roles", ["user"]),
        supabase.from("vietnamcv_profiles").select("id", { count: "exact", head: true }).contains("roles", ["admin"]),
        supabase.from("vietnamcv_profiles").select("id", { count: "exact", head: true }),
      ]);

      setOverviewStats({
        pendingJobs: pendingRes.count ?? 0,
        approvedJobs: approvedRes.count ?? 0,
        totalJobs: jobsRes.count ?? 0,
        totalBusinesses: bizRes.count ?? 0,
        totalUsers: userRes.count ?? 0,
        totalAdmins: adminRes.count ?? 0,
        totalProfiles: totalRes.count ?? 0,
      });
    } catch {
      // silently fail - will show zeros
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOverviewStats();
  }, [fetchOverviewStats]);

  useEffect(() => {
    if (activeTab === "overview") {
      fetchOverviewStats();
    }
  }, [activeTab, fetchOverviewStats]);

  // Luôn refresh profile khi vào trang admin để lấy roles mới nhất từ DB
  useEffect(() => {
    if (isAuthenticated) {
      refreshProfile().finally(() => setProfileVerified(true));
    } else {
      setProfileVerified(true);
    }
  }, [isAuthenticated, refreshProfile]);

  // Close mobile sidebar on tab change
  const handleTabChange = (tab: AdminTab) => {
    setActiveTab(tab);
    handleMobileSidebarClose();
  };

  const handleMobileSidebarClose = () => {
    setMobileSidebarClosing(true);
    setTimeout(() => {
      setMobileSidebarOpen(false);
      setMobileSidebarClosing(false);
    }, 220);
  };

  // Close mobile sidebar on backdrop click
  useEffect(() => {
    if (mobileSidebarOpen || mobileSidebarClosing) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileSidebarOpen, mobileSidebarClosing]);

  const showMobileSidebar = mobileSidebarOpen || mobileSidebarClosing;

  if (loading || !profileVerified) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-foreground-500">Đang xác thực quyền truy cập...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-center px-4">
          <div className="w-16 h-16 flex items-center justify-center bg-red-50 rounded-full">
            <i className="ri-shield-cross-line text-red-500 text-2xl"></i>
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-2">Truy cập bị từ chối</h2>
            <p className="text-sm text-foreground-500 max-w-sm">
              Bạn cần đăng nhập với tài khoản quản trị để truy cập trang này.
            </p>
          </div>
          <Link
            to="/dang-nhap"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors no-underline cursor-pointer whitespace-nowrap"
          >
            <i className="ri-login-box-line text-base"></i>
            <span>Đăng nhập</span>
          </Link>
        </div>
      </div>
    );
  }

  const isAdmin = user.roles.includes("admin");
  const isSuperAdmin = user.email === "admin@vietnam.cv";

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-center px-4">
          <div className="w-16 h-16 flex items-center justify-center bg-red-50 rounded-full">
            <i className="ri-shield-cross-line text-red-500 text-2xl"></i>
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-2">Truy cập bị từ chối</h2>
            <p className="text-sm text-foreground-500 max-w-sm">
              Tài khoản của bạn không có quyền quản trị. Vui lòng liên hệ admin@vietnam.cv nếu cần hỗ trợ.
            </p>
          </div>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors no-underline cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-3-line text-base"></i>
            <span>Về trang chủ</span>
          </Link>
        </div>
      </div>
    );
  }

  const tabs: { key: AdminTab; label: string; icon: string; superOnly?: boolean }[] = [
    { key: "overview", label: "Tổng quan", icon: "ri-dashboard-line" },
    { key: "jobs", label: "Kiểm duyệt tin", icon: "ri-file-list-3-line" },
    { key: "post", label: "Đăng tin", icon: "ri-add-circle-line" },
    { key: "businesses", label: "Doanh nghiệp", icon: "ri-building-line" },
    { key: "users", label: "Người dùng", icon: "ri-user-line" },
    { key: "adminRoles", label: "Phân quyền", icon: "ri-shield-user-line", superOnly: true },
  ];

  const visibleTabs = tabs.filter((t) => !t.superOnly || isSuperAdmin);

  const sidebarContent = (
    <>
      <nav className="p-4 space-y-1">
        {visibleTabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => handleTabChange(tab.key)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
              activeTab === tab.key
                ? "bg-primary-100 text-primary-700"
                : "text-foreground-600 hover:bg-background-100 hover:text-foreground-900"
            }`}
          >
            <i className={`${tab.icon} text-base`}></i>
            <span>{tab.label}</span>
            {tab.key === "jobs" && overviewStats.pendingJobs > 0 && (
              <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 bg-amber-500 text-white text-[10px] font-bold rounded-full px-1">
                {overviewStats.pendingJobs}
              </span>
            )}
          </button>
        ))}
      </nav>

      <div className="px-4 pb-4 mt-4">
        <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider mb-2">Thống kê nhanh</p>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between p-2.5 bg-background-100 rounded-lg">
            <span className="text-xs text-foreground-600 flex items-center gap-1.5">
              <i className="ri-time-line text-amber-500 text-xs"></i>
              Chờ duyệt
            </span>
            {statsLoading ? (
              <span className="w-5 h-4 bg-background-200 animate-pulse rounded"></span>
            ) : (
              <span className="text-sm font-bold text-amber-600">{overviewStats.pendingJobs}</span>
            )}
          </div>
          <div className="flex items-center justify-between p-2.5 bg-background-100 rounded-lg">
            <span className="text-xs text-foreground-600 flex items-center gap-1.5">
              <i className="ri-building-line text-accent-500 text-xs"></i>
              Doanh nghiệp
            </span>
            {statsLoading ? (
              <span className="w-5 h-4 bg-background-200 animate-pulse rounded"></span>
            ) : (
              <span className="text-sm font-bold text-accent-600">{overviewStats.totalBusinesses}</span>
            )}
          </div>
          <div className="flex items-center justify-between p-2.5 bg-background-100 rounded-lg">
            <span className="text-xs text-foreground-600 flex items-center gap-1.5">
              <i className="ri-user-line text-secondary-500 text-xs"></i>
              Người dùng
            </span>
            {statsLoading ? (
              <span className="w-5 h-4 bg-background-200 animate-pulse rounded"></span>
            ) : (
              <span className="text-sm font-bold text-secondary-600">{overviewStats.totalProfiles}</span>
            )}
          </div>
          <div className="flex items-center justify-between p-2.5 bg-background-100 rounded-lg">
            <span className="text-xs text-foreground-600 flex items-center gap-1.5">
              <i className="ri-shield-user-line text-red-500 text-xs"></i>
              Quản trị
            </span>
            {statsLoading ? (
              <span className="w-5 h-4 bg-background-200 animate-pulse rounded"></span>
            ) : (
              <span className="text-sm font-bold text-red-600">{overviewStats.totalAdmins}</span>
            )}
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="flex flex-col h-screen bg-background-50 overflow-hidden">
      {/* Header */}
      <header className="shrink-0 z-30 bg-background-50 border-b border-background-200">
        <div className="flex items-center justify-between px-4 md:px-6 h-16">
          <div className="flex items-center gap-3">
            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileSidebarOpen(true)}
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-menu-line text-xl text-foreground-700"></i>
            </button>

            <Link to="/" className="flex items-center gap-2 no-underline">
              <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center bg-primary-500 rounded-lg">
                <i className="ri-briefcase-line text-white text-base md:text-lg"></i>
              </div>
              <span className="font-heading font-bold text-base md:text-lg text-foreground-950 hidden sm:inline">
                Vietnam<span className="text-primary-500">.cv</span>
              </span>
            </Link>
            <span className="hidden sm:block w-px h-5 bg-background-300"></span>
            <span className="hidden sm:inline text-xs font-semibold text-foreground-500 uppercase tracking-wider">Admin Panel</span>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            <div className={`flex items-center gap-1.5 md:gap-2 px-2.5 py-1.5 border rounded-full ${isSuperAdmin ? "bg-red-50 border-red-200" : "bg-secondary-100 border-secondary-200"}`}>
              <div className={`w-1.5 h-1.5 md:w-2 md:h-2 rounded-full ${isSuperAdmin ? "bg-red-500" : "bg-secondary-500"}`}></div>
              <span className={`text-[10px] md:text-xs font-semibold ${isSuperAdmin ? "text-red-700" : "text-secondary-700"}`}>
                {isSuperAdmin ? "Super Admin" : "Admin"}
              </span>
            </div>
            <span className="hidden md:inline text-xs text-foreground-400">{user.email}</span>
            <Link
              to="/"
              className="flex items-center gap-1 text-sm text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-logout-box-line text-base"></i>
              <span className="hidden sm:inline">Thoát</span>
            </Link>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden relative">
        {/* Desktop sidebar */}
        <aside className="hidden md:flex w-56 h-full border-r border-background-200 bg-background-50 flex-shrink-0 flex-col overflow-y-auto">
          {sidebarContent}
        </aside>

        {/* Mobile sidebar overlay */}
        {showMobileSidebar && (
          <>
            {/* Backdrop */}
            <div
              className={`md:hidden fixed inset-0 z-40 ${mobileSidebarClosing ? "animate-fade-out" : "animate-fade-in"}`}
              style={{ background: mobileSidebarClosing ? "rgba(0,0,0,0)" : "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)" }}
              onClick={handleMobileSidebarClose}
            ></div>
            {/* Slide-in sidebar */}
            <aside className={`md:hidden fixed top-0 left-0 z-50 w-64 h-screen bg-background-50 border-r border-background-200 flex flex-col shadow-2xl ${mobileSidebarClosing ? "animate-slide-out-left" : "animate-slide-in-left"}`}>
              <div className="flex items-center justify-between px-4 h-16 border-b border-background-200 shrink-0">
                <span className="font-heading font-bold text-base text-foreground-950">Admin Panel</span>
                <button
                  onClick={handleMobileSidebarClose}
                  className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-lg text-foreground-600"></i>
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                {sidebarContent}
              </div>
            </aside>
          </>
        )}

        {/* Main content */}
        <main className="flex-1 p-4 md:p-6 w-full overflow-y-auto overflow-x-hidden">
          <div className="mb-5 md:mb-6">
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">
              {activeTab === "overview" && "Tổng quan hệ thống"}
              {activeTab === "jobs" && "Kiểm duyệt tin tuyển dụng"}
              {activeTab === "post" && "Đăng tin tuyển dụng"}
              {activeTab === "businesses" && "Quản lý doanh nghiệp"}
              {activeTab === "users" && "Quản lý người dùng"}
              {activeTab === "adminRoles" && "Phân quyền quản trị"}
            </h2>
            <p className="text-xs md:text-sm text-foreground-500 mt-0.5">
              {activeTab === "overview" && "Xem tổng quan toàn bộ hệ thống — số liệu được cập nhật theo thời gian thực"}
              {activeTab === "jobs" && "Duyệt, từ chối hoặc xóa các tin tuyển dụng trước khi hiển thị công khai"}
              {activeTab === "post" && "Đăng tin tuyển dụng mới trực tiếp — tin sẽ tự động được duyệt và hiển thị công khai"}
              {activeTab === "businesses" && "Xem và quản lý thông tin các doanh nghiệp đã đăng ký tài khoản"}
              {activeTab === "users" && "Xem và quản lý thông tin các cá nhân đã đăng ký tài khoản"}
              {activeTab === "adminRoles" && "Thêm hoặc thu hồi quyền quản trị cho các tài khoản"}
            </p>
          </div>

          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="space-y-4 md:space-y-6">
              {/* Stats cards */}
              <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                <div className="bg-background-50 border border-background-200 rounded-2xl p-4 md:p-5">
                  <div className="flex items-center justify-between mb-2 md:mb-3">
                    <span className="text-[10px] md:text-xs font-semibold text-foreground-500 uppercase tracking-wider">Tổng tin</span>
                    <div className="w-7 h-7 md:w-9 md:h-9 rounded-xl bg-primary-100 flex items-center justify-center">
                      <i className="ri-briefcase-line text-primary-600 text-sm md:text-base"></i>
                    </div>
                  </div>
                  {statsLoading ? (
                    <div className="h-7 md:h-8 w-12 md:w-16 bg-background-200 animate-pulse rounded-lg"></div>
                  ) : (
                    <p className="text-xl md:text-2xl font-heading font-bold text-foreground-950">{overviewStats.totalJobs}</p>
                  )}
                  <div className="flex items-center gap-2 md:gap-3 mt-1.5 md:mt-2">
                    <span className="text-[10px] md:text-xs text-green-600 font-medium">{overviewStats.approvedJobs} duyệt</span>
                    <span className="w-px h-3 bg-background-300"></span>
                    <span className="text-[10px] md:text-xs text-amber-600 font-medium">{overviewStats.pendingJobs} chờ</span>
                  </div>
                </div>

                <div className="bg-background-50 border border-background-200 rounded-2xl p-4 md:p-5">
                  <div className="flex items-center justify-between mb-2 md:mb-3">
                    <span className="text-[10px] md:text-xs font-semibold text-foreground-500 uppercase tracking-wider">Doanh nghiệp</span>
                    <div className="w-7 h-7 md:w-9 md:h-9 rounded-xl bg-accent-100 flex items-center justify-center">
                      <i className="ri-building-line text-accent-600 text-sm md:text-base"></i>
                    </div>
                  </div>
                  {statsLoading ? (
                    <div className="h-7 md:h-8 w-12 md:w-16 bg-background-200 animate-pulse rounded-lg"></div>
                  ) : (
                    <p className="text-xl md:text-2xl font-heading font-bold text-foreground-950">{overviewStats.totalBusinesses}</p>
                  )}
                  <p className="text-[10px] md:text-xs text-foreground-500 mt-1.5 md:mt-2">Đã đăng ký</p>
                </div>

                <div className="bg-background-50 border border-background-200 rounded-2xl p-4 md:p-5">
                  <div className="flex items-center justify-between mb-2 md:mb-3">
                    <span className="text-[10px] md:text-xs font-semibold text-foreground-500 uppercase tracking-wider">Tìm việc</span>
                    <div className="w-7 h-7 md:w-9 md:h-9 rounded-xl bg-secondary-100 flex items-center justify-center">
                      <i className="ri-user-line text-secondary-600 text-sm md:text-base"></i>
                    </div>
                  </div>
                  {statsLoading ? (
                    <div className="h-7 md:h-8 w-12 md:w-16 bg-background-200 animate-pulse rounded-lg"></div>
                  ) : (
                    <p className="text-xl md:text-2xl font-heading font-bold text-foreground-950">{overviewStats.totalUsers}</p>
                  )}
                  <p className="text-[10px] md:text-xs text-foreground-500 mt-1.5 md:mt-2">Cá nhân đăng ký</p>
                </div>

                <div className="bg-background-50 border border-background-200 rounded-2xl p-4 md:p-5">
                  <div className="flex items-center justify-between mb-2 md:mb-3">
                    <span className="text-[10px] md:text-xs font-semibold text-foreground-500 uppercase tracking-wider">Admin</span>
                    <div className="w-7 h-7 md:w-9 md:h-9 rounded-xl bg-red-100 flex items-center justify-center">
                      <i className="ri-shield-user-line text-red-500 text-sm md:text-base"></i>
                    </div>
                  </div>
                  {statsLoading ? (
                    <div className="h-7 md:h-8 w-12 md:w-16 bg-background-200 animate-pulse rounded-lg"></div>
                  ) : (
                    <p className="text-xl md:text-2xl font-heading font-bold text-foreground-950">{overviewStats.totalAdmins}</p>
                  )}
                  <p className="text-[10px] md:text-xs text-foreground-500 mt-1.5 md:mt-2">Quản trị viên</p>
                </div>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
                <div className="bg-background-50 border border-background-200 rounded-2xl p-4 md:p-5">
                  <h3 className="text-xs md:text-sm font-semibold text-foreground-800 mb-3 md:mb-4">Phân bố tin tuyển dụng</h3>
                  <div className="space-y-2.5 md:space-y-3">
                    <div>
                      <div className="flex items-center justify-between mb-1 md:mb-1.5">
                        <span className="text-[11px] md:text-xs text-foreground-600 flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-green-500 inline-block"></span>
                          Đã duyệt
                        </span>
                        <span className="text-[11px] md:text-xs font-semibold text-foreground-800">{overviewStats.approvedJobs}</span>
                      </div>
                      <div className="w-full h-2 bg-background-100 rounded-full overflow-hidden">
                        <div className="h-full bg-green-500 rounded-full transition-all duration-500" style={{ width: overviewStats.totalJobs > 0 ? `${(overviewStats.approvedJobs / overviewStats.totalJobs) * 100}%` : "0%" }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1 md:mb-1.5">
                        <span className="text-[11px] md:text-xs text-foreground-600 flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-amber-500 inline-block"></span>
                          Chờ duyệt
                        </span>
                        <span className="text-[11px] md:text-xs font-semibold text-foreground-800">{overviewStats.pendingJobs}</span>
                      </div>
                      <div className="w-full h-2 bg-background-100 rounded-full overflow-hidden">
                        <div className="h-full bg-amber-500 rounded-full transition-all duration-500" style={{ width: overviewStats.totalJobs > 0 ? `${(overviewStats.pendingJobs / overviewStats.totalJobs) * 100}%` : "0%" }}></div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-background-50 border border-background-200 rounded-2xl p-4 md:p-5">
                  <h3 className="text-xs md:text-sm font-semibold text-foreground-800 mb-3 md:mb-4">Phân bố người dùng</h3>
                  <div className="space-y-2.5 md:space-y-3">
                    {[
                      { label: "Người tìm việc", count: overviewStats.totalUsers, color: "bg-secondary-500" },
                      { label: "Doanh nghiệp", count: overviewStats.totalBusinesses, color: "bg-accent-500" },
                      { label: "Quản trị viên", count: overviewStats.totalAdmins, color: "bg-red-500" },
                    ].map((item) => (
                      <div key={item.label}>
                        <div className="flex items-center justify-between mb-1 md:mb-1.5">
                          <span className="text-[11px] md:text-xs text-foreground-600 flex items-center gap-1.5">
                            <span className={`w-2.5 h-2.5 md:w-3 md:h-3 rounded-full ${item.color} inline-block`}></span>
                            {item.label}
                          </span>
                          <span className="text-[11px] md:text-xs font-semibold text-foreground-800">{item.count}</span>
                        </div>
                        <div className="w-full h-2 bg-background-100 rounded-full overflow-hidden">
                          <div className={`h-full ${item.color} rounded-full transition-all duration-500`} style={{ width: (overviewStats.totalBusinesses + overviewStats.totalUsers + overviewStats.totalAdmins) > 0 ? `${(item.count / (overviewStats.totalBusinesses + overviewStats.totalUsers + overviewStats.totalAdmins)) * 100}%` : "0%" }}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Quick actions */}
              <div className="bg-background-50 border border-background-200 rounded-2xl p-4 md:p-5">
                <h3 className="text-xs md:text-sm font-semibold text-foreground-800 mb-3 md:mb-4">Thao tác nhanh</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 md:gap-3">
                  {[
                    { tab: "jobs" as AdminTab, icon: "ri-file-list-3-line", label: "Kiểm duyệt tin", color: "bg-amber-100 text-amber-600" },
                    { tab: "post" as AdminTab, icon: "ri-add-circle-line", label: "Đăng tin mới", color: "bg-primary-100 text-primary-600" },
                    { tab: "businesses" as AdminTab, icon: "ri-building-line", label: "Doanh nghiệp", color: "bg-accent-100 text-accent-600" },
                    { tab: "users" as AdminTab, icon: "ri-user-line", label: "Người dùng", color: "bg-secondary-100 text-secondary-600" },
                  ].map((action) => (
                    <button
                      key={action.tab}
                      onClick={() => handleTabChange(action.tab)}
                      className="flex flex-col items-center gap-2 p-3 md:p-4 bg-background-100 rounded-xl hover:opacity-80 transition-opacity cursor-pointer"
                    >
                      <div className={`w-8 h-8 md:w-10 md:h-10 rounded-xl ${action.color} flex items-center justify-center`}>
                        <i className={`${action.icon} text-sm md:text-lg`}></i>
                      </div>
                      <span className="text-[10px] md:text-xs font-semibold text-foreground-700 whitespace-nowrap">{action.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === "jobs" && <JobModeration />}
          {activeTab === "post" && <PostJobForm />}
          {activeTab === "businesses" && <BusinessManagement />}
          {activeTab === "users" && <UserManagement />}
          {activeTab === "adminRoles" && <AdminRoleManagement />}
        </main>
      </div>
    </div>
  );
}